/**
 * postLoginIntent — remembers where a guest was headed when a Google
 * sign-in interrupts them, so the app can put them back there afterwards.
 *
 * Stored in localStorage, NOT sessionStorage: on a phone the Google
 * sign-in backgrounds the PWA, and iOS/Android routinely kill and
 * relaunch it — sessionStorage does not survive that relaunch (which is
 * exactly how the old flag got lost and guests landed back on the
 * Jukebox). Firebase auth persists in IndexedDB across the same kill,
 * so the intent must persist at least as well.
 *
 * Every entry carries a timestamp and is consumed (removed) on read.
 * The TTL keeps a stale intent from hijacking a normal visit days later.
 */

const PANEL_KEY = 'muuzbox_post_login_panel_v2';
const JOIN_KEY = 'muuzbox_post_login_join_v2';
const TTL_MS = 20 * 60 * 1000; // 20 minutes — spans any realistic sign-in round-trip

const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...value, at: Date.now() }));
  } catch (_) {}
};

const consume = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    localStorage.removeItem(key);
    const data = JSON.parse(raw);
    if (!data?.at || Date.now() - data.at > TTL_MS) return null;
    return data;
  } catch (_) {
    return null;
  }
};

/**
 * The guest was on (or heading to) a panel when sign-in started.
 * `channel` is the venue (URL-hash channel name): the installed PWA's
 * start_url has NO #channel, so when Android hands the post-sign-in
 * navigation to the installed app instead of the Chrome tab, the venue
 * context would otherwise be lost along with the panel.
 */
export const rememberPostLoginPanel = (panel, channel = null) => {
  write(PANEL_KEY, { panel, channel: channel || null });
  // When this app is EMBEDDED (the reports-admin "Muuzbox Player" PWA wraps
  // it in an iframe on a DIFFERENT origin), our localStorage is invisible to
  // the parent — yet the PARENT decides what screen shows after a sign-in
  // kills and relaunches the PWA (it boots to its audio player by default).
  // Relay the intent up so the parent can bring the user back to the
  // requests view, where this iframe reloads, finds its own copy of the
  // flag, and opens Games. Payload is non-sensitive (a panel name and a
  // public channel name), hence targetOrigin '*' — same trust model as the
  // existing muuzbox-user-info message.
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        { type: "muuzbox-post-login-intent", panel, channel: channel || null, at: Date.now() },
        "*"
      );
    }
  } catch (_) {}
};

/** One-shot read of the pending intent ({ panel, channel } | null). */
export const consumePostLoginPanel = () => {
  const data = consume(PANEL_KEY);
  return data?.panel ? { panel: data.panel, channel: data.channel || null } : null;
};

/** Non-destructive read of the pending intent's channel (App boot needs it
 *  BEFORE the React tree mounts and consumes the intent). */
export const peekPostLoginChannel = () => {
  try {
    const raw = localStorage.getItem(PANEL_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.at || Date.now() - data.at > TTL_MS) return null;
    return data.channel || null;
  } catch (_) {
    return null;
  }
};

/**
 * The guest tapped "Join tonight" and got sent to Google sign-in —
 * remember the tap (and their table choice) so the join completes by
 * itself once they come back signed in, even if the PWA was relaunched.
 */
export const rememberPostLoginJoin = (tableId) => write(JOIN_KEY, { tableId: tableId || null });

/** One-shot read of the pending join ({ tableId } | null). */
export const consumePostLoginJoin = () => {
  const data = consume(JOIN_KEY);
  return data ? { tableId: data.tableId || null } : null;
};

/** Drop a pending join (the popup flow completed it in-page). */
export const clearPostLoginJoin = () => {
  try { localStorage.removeItem(JOIN_KEY); } catch (_) {}
};
